import React, { useEffect, useState } from 'react';
import {
  Typography,
  Stack,
  Divider,
  Card,
  CardContent,
  Button,
  Box,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase/firebase';
import { getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import ErrorDialog from '../../../components/global/ErrorDialog';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { employeeCountMap, frontDeskPlan, isDowngrade, planIdMap, planNameMap, plans } from './planConfig';
import { useTenant } from '../../../tenancy/TenantContext';
import { useConfirmOnboardingPlan, useLocalTenantSubscription } from '../../../sitePublishing/hooks';
import { OnboardingPlanKey } from '../../../sitePublishing/types';
import { launchActionButtonSx, NumericStepper, QuestionCard } from '../Onboarding/OnboardingPrimitives';

const STATUS_DISPLAY: Record<string, { label: string; color: 'default' | 'success' | 'info' | 'warning' | 'error' }> = {
  inactive: { label: 'Not started', color: 'default' },
  trialing: { label: 'Free trial', color: 'info' },
  trial: { label: 'Free trial', color: 'info' },
  active: { label: 'Active', color: 'success' },
  past_due: { label: 'Payment past due', color: 'warning' },
  unpaid: { label: 'Unpaid', color: 'error' },
  canceled: { label: 'Canceled', color: 'error' },
  incomplete: { label: 'Checkout not finished', color: 'warning' },
  incomplete_expired: { label: 'Checkout expired', color: 'error' },
};

export const subscriptionStatusDisplay = (status: string) =>
  STATUS_DISPLAY[status] ?? { label: status.replace(/_/g, ' '), color: 'default' as const };

const SubscriptionStatus: React.FC<{ status: string; periodEnd: string | null }> = ({ status, periodEnd }) => {
  const display = subscriptionStatusDisplay(status);
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color="text.secondary" fontWeight={700}>Subscription</Typography>
      <Chip size="small" label={display.label} color={display.color} variant={display.color === 'default' ? 'outlined' : 'filled'}
        sx={{ fontWeight: 800 }} />
      {periodEnd ? (
        <Typography variant="body2" color="text.secondary">
          {['trialing', 'trial'].includes(status) ? 'Trial ends' : 'Renews or ends'} {periodEnd}
        </Typography>
      ) : null}
    </Stack>
  );
};



type ManageSubscriptionPageProps = {
  onboardingMode?: 'team' | 'front_desk';
  onboardingPlanKey?: OnboardingPlanKey;
  onboardingCapacity?: number;
  /** Onboarding only: the server already counts billing as handled. */
  onboardingPaymentComplete?: boolean;
  /** Onboarding only: move on once no subscription is needed. */
  onContinue?: () => void;
};

const ManageSubscriptionPage: React.FC<ManageSubscriptionPageProps> = ({
  onboardingMode, onboardingPlanKey, onboardingCapacity, onboardingPaymentComplete, onContinue,
}) => {
  const { tenantId } = useTenant();
  const localSubscriptionMutation = useLocalTenantSubscription();
  const confirmPlanMutation = useConfirmOnboardingPlan();
  const [checkoutMode, setCheckoutMode] = useState<'employees' | 'front_desk'>('employees');

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [selectedEmployees, setSelectedEmployees] = useState(onboardingCapacity ?? 1);
  const selectableEmployeeCounts = [1, 2, 3, 4, 5];

  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
    // Billing can be changed by Scheduify at any time, so never show an old copy here.
    refetchOnMount: 'always',
  });

  const paymentInfo = businessConfigQuery.data?.paymentInfo;
  const founderPlanKey = paymentInfo?.pendingPlanKey;
  const plan = paymentInfo?.planType ?? 'free';
  const status = paymentInfo?.subscriptionStatus ?? 'inactive';
  const hasExistingSubscription = ['active', 'trialing', 'trial', 'past_due', 'unpaid'].includes(status);
  const subscriptionWaived = paymentInfo?.skipSubscriptionCheck === true ||
    (onboardingPaymentComplete === true && !hasExistingSubscription);
  const currentPeriodEnd = paymentInfo?.currentPeriodEnd
    ? dayjs(paymentInfo.currentPeriodEnd).format('MMMM D, YYYY')
    : null;

  const [redirecting, setRedirecting] = useState(
    !!localStorage.getItem('redirectingToStripe')
  );

  const openStripePortalMutation = useMutation({
    mutationFn: async () => {
      const callable = httpsCallable<
        { websiteId: string; returnUrl: string },
        { url: string }
      >(functions, 'createStripePortal');

      const result = await callable({
        websiteId: tenantId,
        returnUrl: window.location.href,
      });
      localStorage.setItem('redirectingToStripe', 'true');

      window.location.href = result.data.url;
    },
  });

  const createStripeCustomerMutation = useMutation({
    mutationFn: async ({ websiteId }: { websiteId: string }) => {
      const callable = httpsCallable<
        { websiteId: string },
        { customerId: string }
      >(functions, 'createStripeCustomer');

      return await callable({ websiteId });
    },
    onSuccess: () => {
      businessConfigQuery.refetch();
    },
  });

  const startCheckoutMutation = useMutation({
    mutationFn: async ({
      planId,
      websiteId,
      returnUrl,
      checkoutMode,
      addOnIds = [],
    }: {
      planId: string;
      websiteId: string;
      returnUrl: string;
      checkoutMode: 'employees' | 'front_desk';
      addOnIds?: string[];
    }) => {
      const callable = httpsCallable<
        { planId: string; websiteId: string; returnUrl: string; checkoutMode: 'employees' | 'front_desk'; addOnIds?: string[] },
        { url: string }
      >(functions, 'startStripeCheckout');

      const result = await callable({ planId, websiteId, returnUrl, checkoutMode, addOnIds });
      localStorage.setItem('redirectingToStripe', 'true');

      window.location.href = result.data.url;
    },
    onError: (err: any) => {
      const message =
        err?.message?.includes('already') || err?.message?.includes('409')
          ? 'You already have an active subscription. Please use the "Manage Subscription" button instead.'
          : 'Something went wrong while starting checkout.';
      setErrorMessage(message);
      setOpenErrorDialog(true);
    },
  });

  const handleStartSubscription = (planId: string, selectedCheckoutMode: 'employees' | 'front_desk') => {
    const currentPlanId = paymentInfo?.planType;
    if (currentPlanId && isDowngrade(currentPlanId, planId)) {
      setErrorMessage("Downgrading your plan is not available through this interface. Please contact support.");
      setOpenErrorDialog(true);
      return;
    }
    if (!paymentInfo?.stripeCustomerId) {
      createStripeCustomerMutation.mutate(
        { websiteId: tenantId },
        {
          onSuccess: () => {
            startCheckoutMutation.mutate({
              planId,
              websiteId: tenantId,
              returnUrl: window.location.href,
              checkoutMode: selectedCheckoutMode,
            });

          },
          onError: () => {
            setErrorMessage('Failed to create Stripe customer.');
            setOpenErrorDialog(true);
          },
        }
      );
    } else {
      startCheckoutMutation.mutate({
        planId,
        websiteId: tenantId,
        returnUrl: window.location.href,
        checkoutMode: selectedCheckoutMode,
      });
    }
  };

  const seatPlanCandidates = plans.filter((candidatePlan) => candidatePlan.id);
  const getPlanForEmployees = (employeeCount: number) =>
    seatPlanCandidates.find((candidatePlan) => (employeeCountMap[candidatePlan.id] ?? 0) >= employeeCount);
  const selectedPlan = getPlanForEmployees(selectedEmployees);
  const selectedPlanId = selectedPlan?.id ?? '';
  const selectedPlanSeats = selectedPlanId ? employeeCountMap[selectedPlanId] ?? 0 : 0;
  const isSelectedPlanDowngrade = selectedPlanId
    ? isDowngrade(paymentInfo?.planType, selectedPlanId)
    : false;
  const lockedPlanKey = onboardingPlanKey ?? founderPlanKey;
  const effectiveOnboardingMode = onboardingMode ?? (founderPlanKey === 'frontDesk' ? 'front_desk' : founderPlanKey ? 'team' : undefined);
  const onboardingPlanId = lockedPlanKey ? planIdMap[lockedPlanKey] : undefined;
  // Team owners choose their size until a subscription exists. A plan that was
  // already confirmed or prepared sets the minimum; they can only add to it.
  const lockedTeamSize = onboardingMode === 'team' && onboardingPlanKey
    ? Math.max(1, onboardingCapacity ?? employeeCountMap[planIdMap[onboardingPlanKey] ?? ''] ?? 1)
    : 1;
  const canChooseTeamSize = onboardingMode === 'team' && !hasExistingSubscription;
  const teamSizeChanged = canChooseTeamSize && (!onboardingPlanKey || selectedEmployees !== lockedTeamSize);
  useEffect(() => {
    if (selectedEmployees < lockedTeamSize) setSelectedEmployees(lockedTeamSize);
  }, [selectedEmployees, lockedTeamSize]);
  const onboardingPlan = lockedPlanKey === 'frontDesk' || effectiveOnboardingMode === 'front_desk' ? frontDeskPlan :
    canChooseTeamSize ? selectedPlan : plans.find((candidate) => candidate.id === onboardingPlanId);
  const effectiveOnboardingPlanId = onboardingPlan?.id;
  const isOnboarding = Boolean(effectiveOnboardingMode);
  const beginOnboardingTrial = async () => {
    if (teamSizeChanged) {
      await confirmPlanMutation.mutateAsync({ teamSize: selectedEmployees });
    }
    if (effectiveOnboardingPlanId) {
      handleStartSubscription(effectiveOnboardingPlanId, effectiveOnboardingMode === 'front_desk' ? 'front_desk' : 'employees');
    }
  };
  const beginLocalTrial = async () => {
    if (teamSizeChanged) {
      await confirmPlanMutation.mutateAsync({ teamSize: selectedEmployees });
    }
    localSubscriptionMutation.mutate();
  };

  useEffect(() => {
    if (redirecting) {
      localStorage.removeItem('redirectingToStripe');
      setRedirecting(false);
    }
  }, []);

  if (redirecting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" color="text.secondary">
            Possible Changes Detected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please refresh the page to see updated subscription status.
          </Typography>
        </Stack>
      </Box>
    );
  }


  return (
    <Box sx={{ px: 2, py: 4, maxWidth: 1000, mx: 'auto' }}>
      <ErrorDialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        message={errorMessage}
      />

      {!isOnboarding ? <Typography variant="h5" fontWeight={700} mb={3}>Manage Subscription</Typography> : null}

      {process.env.REACT_APP_ENV === 'local' && !hasExistingSubscription && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={(
            <Button
              color="inherit"
              onClick={() => void beginLocalTrial()}
              disabled={localSubscriptionMutation.isPending || confirmPlanMutation.isPending}
            >
              {localSubscriptionMutation.isPending ? 'Activating...' : 'Activate test trial'}
            </Button>
          )}
        >
          Local emulator mode can mark this tenant trialing without contacting Stripe.
        </Alert>
      )}
      {(localSubscriptionMutation.isError || confirmPlanMutation.isError) && (
        <Alert severity="error" sx={{ mb: 3 }}>{localSubscriptionMutation.error?.message ?? confirmPlanMutation.error?.message}</Alert>
      )}

      {!paymentInfo?.skipSubscriptionCheck && !isOnboarding && (
        <Stack spacing={1.5} mb={4}>
          {!isOnboarding ? <Typography color="text.secondary">
            {!currentPeriodEnd ? (
              <>You haven't started a subscription yet. Please select a plan to activate your website.</>
            ) : hasExistingSubscription && planNameMap[plan] ? (
              <>Currently on the <strong>{planNameMap[plan]}</strong>.</>
            ) : (
              <>Not enrolled in a plan. Website will be active until <strong>{currentPeriodEnd}</strong>.</>
            )}
          </Typography> : null}


          {!isOnboarding ? <SubscriptionStatus status={status} periodEnd={currentPeriodEnd} /> : null}

          {!isOnboarding ? <Box
            sx={{
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              All plans include
            </Typography>
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.secondary">✅ Custom booking website</Typography>
              <Typography variant="body2" color="text.secondary">✅ Employee Portal</Typography>
              <Typography variant="body2" color="text.secondary">✅ Online Payments</Typography>
              <Typography variant="body2" color="text.secondary">✅ SMS/Email confirmations for customers and employees</Typography>
              <Typography variant="body2" color="text.secondary">✅ Custom domain (yourname.scheduify.com)</Typography>
              <Typography variant="body2" color="text.secondary">✅ Scheduify Customer Support</Typography>
            </Stack>
          </Box> : null}
        </Stack>
      )}

      {!isOnboarding ? <Divider sx={{ mb: 4 }} /> : null}

      {isOnboarding && subscriptionWaived ? (
        <QuestionCard eyebrow="Subscription" title="No subscription needed"
          detail="Scheduify has turned off billing for your business. There is nothing to pay or set up.">
          <Stack spacing={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleRounded color="success" />
              <Typography fontWeight={750}>You're all set here.</Typography>
            </Stack>
            {onContinue ? (
              <Button variant="contained" size="large" onClick={onContinue}
                sx={{ ...launchActionButtonSx, alignSelf: { xs: 'stretch', sm: 'flex-start' }, px: 4 }}>
                Continue to launch
              </Button>
            ) : null}
          </Stack>
        </QuestionCard>
      ) : isOnboarding ? (
        <QuestionCard
          eyebrow="Subscription"
          title={hasExistingSubscription ? 'Your subscription' : 'Start your free trial'}
          detail={hasExistingSubscription
            ? 'Your plan is set. Use Manage subscription to update billing details.'
            : 'Try everything free for 30 days. You can cancel any time before the trial ends.'}
        >
          <Stack spacing={2.5}>
            <SubscriptionStatus status={status} periodEnd={currentPeriodEnd} />
            <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3.5, border: '2px solid', borderColor: 'primary.main',
              bgcolor: 'rgba(25,118,210,.04)' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                <Box>
                  <Typography variant="overline" color="primary.main" fontWeight={900} letterSpacing=".08em">Your plan</Typography>
                  <Typography variant="h5" fontWeight={900}>{onboardingPlan?.title ?? onboardingPlanKey ?? 'Choose your team size'}</Typography>
                  <Typography color="text.secondary" sx={{ mt: .5 }}>
                    {effectiveOnboardingMode === 'front_desk'
                      ? 'One central admin login and up to 8 bookable resources.'
                      : canChooseTeamSize
                        ? `Supports ${selectedEmployees} ${selectedEmployees === 1 ? 'employee login' : 'employee logins'}.` +
                          (onboardingPlanKey ? ' You can add more employees before your trial starts.' : '')
                        : `Supports up to ${employeeCountMap[effectiveOnboardingPlanId ?? ''] ?? lockedTeamSize} employee logins.`}
                  </Typography>
                </Box>
                {onboardingPlan?.price ? <Typography variant="h4" fontWeight={900} color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                  {onboardingPlan.price}
                </Typography> : null}
              </Stack>
            </Box>
            {canChooseTeamSize ? (
              <NumericStepper label="Employees who need logins" value={selectedEmployees}
                min={lockedTeamSize} max={25} onChange={setSelectedEmployees} />
            ) : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {Array.from(new Set([
                ...(onboardingPlan?.features ?? []),
                'Online payments',
                'Custom booking website',
                'Scheduify customer support',
              ])).map((feature) => (
                <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleRounded color="success" sx={{ fontSize: 20, mt: '1px' }} />
                  <Typography variant="body2">{feature}</Typography>
                </Stack>
              ))}
            </Box>
            {openStripePortalMutation.isError ? <Alert severity="error">{openStripePortalMutation.error instanceof Error ? openStripePortalMutation.error.message : 'Could not open the billing portal.'}</Alert> : null}
            {!hasExistingSubscription ? (
              <Button variant="contained" size="large"
                disabled={!effectiveOnboardingPlanId || startCheckoutMutation.isPending || createStripeCustomerMutation.isPending || confirmPlanMutation.isPending}
                onClick={() => void beginOnboardingTrial()}
                sx={{ ...launchActionButtonSx, alignSelf: { xs: 'stretch', sm: 'flex-start' }, px: 4 }}>
                {startCheckoutMutation.isPending ? 'Opening checkout...' : 'Start your 30-day trial'}
              </Button>
            ) : !paymentInfo?.localEmulatorOnly ? (
              <Button variant="contained" size="large" onClick={() => openStripePortalMutation.mutate()}
                disabled={openStripePortalMutation.isPending}
                sx={{ ...launchActionButtonSx, alignSelf: { xs: 'stretch', sm: 'flex-start' }, px: 4 }}>
                Manage subscription
              </Button>
            ) : null}
          </Stack>
        </QuestionCard>
      ) : hasExistingSubscription ? (
        <Box textAlign="center">
          {paymentInfo?.localEmulatorOnly ? (
            <Alert severity="info">Billing is simulated locally. The Stripe customer portal is not available in emulator mode.</Alert>
          ) : (
          <Button
            variant="contained"
            onClick={() => openStripePortalMutation.mutate()}
            sx={{ py: 1.5, fontWeight: 600, minWidth: 250 }}
          >
            Manage Subscription
          </Button>
          )}
          {openStripePortalMutation.isError ? <Alert severity="error" sx={{ mt: 2 }}>{openStripePortalMutation.error instanceof Error ? openStripePortalMutation.error.message : 'Could not open the billing portal.'}</Alert> : null}
        </Box>
      ) : (
        <Stack spacing={3}>
          {paymentInfo?.skipSubscriptionCheck ? (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold">
                  No subscription needed
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Scheduify has turned off billing for your business. There is nothing to pay or set up.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Stack spacing={3} alignItems="center" textAlign="center">
                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      Choose your subscription plan
                    </Typography>
                    <Typography color="text.secondary">
                      Select employee count or choose Front Desk Mode.
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} width="100%" maxWidth={420}>
                    <Button
                      fullWidth
                      variant={checkoutMode === 'employees' ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={() => setCheckoutMode('employees')}
                    >
                      Employees
                    </Button>
                    <Button
                      fullWidth
                      variant={checkoutMode === 'front_desk' ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={() => setCheckoutMode('front_desk')}
                    >
                      Front Desk Mode
                    </Button>
                  </Stack>

                  {checkoutMode === 'employees' ? (
                    <>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton
                          aria-label="Decrease employee count"
                          onClick={() =>
                            setSelectedEmployees((prev) => {
                              const currentIndex = selectableEmployeeCounts.indexOf(prev);
                              const nextIndex = Math.max(0, currentIndex - 1);
                              return selectableEmployeeCounts[nextIndex];
                            })
                          }
                          disabled={selectedEmployees === selectableEmployeeCounts[0]}
                          color="secondary"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <RemoveIcon />
                        </IconButton>

                        <Stack minWidth={170}>
                          <Typography variant="overline" color="text.secondary">
                            Employees
                          </Typography>
                          <Typography variant="h4" fontWeight={700}>
                            {selectedEmployees}
                          </Typography>
                        </Stack>

                        <IconButton
                          aria-label="Increase employee count"
                          onClick={() =>
                            setSelectedEmployees((prev) => {
                              const currentIndex = selectableEmployeeCounts.indexOf(prev);
                              const nextIndex = Math.min(selectableEmployeeCounts.length - 1, currentIndex + 1);
                              return selectableEmployeeCounts[nextIndex];
                            })
                          }
                          disabled={selectedEmployees === selectableEmployeeCounts[selectableEmployeeCounts.length - 1]}
                          color="secondary"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>

                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {selectedPlan ? `${selectedPlan.title} ${selectedPlan.price}` : 'No plan available'}
                        </Typography>
                        {selectedPlanSeats > selectedEmployees && (
                          <Typography variant="body2" color="text.secondary">
                            Includes up to {selectedPlanSeats} employees
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {frontDeskPlan ? `${frontDeskPlan.title} ${frontDeskPlan.price}` : 'Front Desk Mode plan unavailable'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        1 admin login, orchestrator booking flow, and front-desk notification routing.
                      </Typography>
                    </Stack>
                  )}

                  <Stack spacing={1} width="100%" maxWidth={360}>
                    {checkoutMode === 'employees' ? (
                      <>
                        <Button
                          fullWidth
                          variant="contained"
                          color="secondary"
                          disabled={!selectedPlanId || isSelectedPlanDowngrade}
                          onClick={() => selectedPlanId && handleStartSubscription(selectedPlanId, 'employees')}
                          sx={{ py: 1 }}
                        >
                          {isSelectedPlanDowngrade ? 'DOWNGRADE UNAVAILABLE' : 'SELECT PLAN'}
                        </Button>
                        {isSelectedPlanDowngrade && (
                          <Typography variant="caption" color="text.secondary" textAlign="center">
                            Downgrading is unavailable here. Contact Scheduify support to change your plan.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        disabled={!frontDeskPlan?.id}
                        onClick={() => {
                          if (!frontDeskPlan?.id) {
                            return;
                          }
                          handleStartSubscription(frontDeskPlan.id, 'front_desk');
                        }}
                        sx={{ py: 1 }}
                      >
                        {frontDeskPlan?.id ? 'SELECT FRONT DESK MODE' : 'FRONT DESK PLAN NOT CONFIGURED'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );


};

export default ManageSubscriptionPage;
